// Player B health telemetry client.
//
// Buffers player-related events (mpegts.js lifecycle, video element state,
// retry attempts, slotAvailable handling) and ships them in batches to the
// backend /api/v1/user/player-health endpoint. Designed to never interfere
// with playback:
//
//  - Events queue in memory (max 100) and flush on:
//      * Queue reaching flushThreshold (20)
//      * Periodic flush timer (every 1 s while there's data)
//      * pagehide / beforeunload via authenticated fetch keepalive
//  - Every request carries the current Bearer token. A batch stays queued
//    until the backend accepts it; a transient failure never discards events.
//  - Failures are swallowed (we never want telemetry to throw into the UI).
//
// Public API:
//   import { playerHealth } from 'utils/playerHealth';
//   playerHealth.setContext({ viewId, publicPath, channelName });
//   playerHealth.report('mpegts.error', { code: '...', message: '...' });
//   playerHealth.flush();   // manual flush, returns Promise<void>

const FLUSH_INTERVAL_MS = 1000;
const FLUSH_THRESHOLD = 20;
const MAX_BATCH = 20;
const MAX_QUEUE = 100;
const MAX_PAYLOAD_BYTES = 60 * 1024;
const MAX_ATTR_NODES = 200;
const ENDPOINT = '/api/v1/user/player-health';
const SENSITIVE_ATTR_KEYS = new Set([
  'authorization',
  'accesstoken',
  'refreshtoken',
  'token',
  'cookie',
  'cookies',
  'header',
  'headers',
  'httpheaders',
  'url',
  'uri',
  'playurl',
  'originurl',
  'upstreamurl',
  'urlwithvid',
  'referer',
  'referrer',
  'clientip',
  'ip',
  'remoteaddr',
  'remoteip',
  'remoteaddress',
  'useragent'
]);

function isSensitiveAttrKey(key) {
  const normalized = String(key)
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');
  return SENSITIVE_ATTR_KEYS.has(normalized);
}

function isIpLiteral(value) {
  let candidate = value.trim();
  if (candidate.startsWith('[') && candidate.endsWith(']')) {
    candidate = candidate.slice(1, -1);
  }

  const ipv4 = candidate.split('.');
  if (ipv4.length === 4 && ipv4.every((part) => /^\d{1,3}$/.test(part) && Number(part) <= 255)) {
    return true;
  }

  // Validate the structural form of an IPv6 literal without pulling a
  // networking dependency into the telemetry path. Embedded IPv4 occupies
  // two of IPv6's eight 16-bit groups.
  const zoneSeparator = candidate.indexOf('%');
  if (zoneSeparator !== -1) candidate = candidate.slice(0, zoneSeparator);
  if (!candidate.includes(':') || !/^[0-9a-f:.]+$/i.test(candidate)) return false;
  if ((candidate.match(/::/g) || []).length > 1) return false;

  const hasCompression = candidate.includes('::');
  const parts = candidate.split(':').filter(Boolean);
  let groups = 0;
  for (const part of parts) {
    if (part.includes('.')) {
      const embedded = part.split('.');
      if (
        embedded.length !== 4 ||
        !embedded.every((item) => /^\d{1,3}$/.test(item) && Number(item) <= 255)
      ) {
        return false;
      }
      groups += 2;
    } else {
      if (!/^[0-9a-f]{1,4}$/i.test(part)) return false;
      groups += 1;
    }
  }

  return hasCompression ? groups < 8 : groups === 8;
}

function sanitizeAttrString(value) {
  if (isIpLiteral(value)) return '[redacted-ip]';
  return value
    .replace(/https?:\/\/[^\s"'<>]+/gi, '[redacted-url]')
    .replace(/\/play\/[^/\s]+\/[A-Za-z0-9._~+%/=-]+/g, '/play/[redacted]')
    .replace(/\bBearer\s+[^\s"'<>]+/gi, 'Bearer [redacted]');
}

function jsonByteLength(value) {
  const serialized = JSON.stringify(value);
  if (typeof TextEncoder !== 'undefined') return new TextEncoder().encode(serialized).length;
  if (typeof Blob !== 'undefined') return new Blob([serialized]).size;
  return unescape(encodeURIComponent(serialized)).length;
}

function boundedString(value, maxLength) {
  return typeof value === 'string' ? value.substring(0, maxLength) : value;
}

class PlayerHealthClient {
  constructor() {
    this.queue = [];
    this.context = {viewId: null, publicPath: null, channelName: null};
    this.flushTimer = null;
    this.flushPromise = null;
    this.flushFailures = 0;
    this.installUnloadHook();
  }

  /** Update the correlation context. All subsequent events inherit these. */
  setContext(partial) {
    this.context = {...this.context, ...partial};
  }

  /** Clear the context (e.g. when leaving the player). */
  clearContext() {
    this.context = {viewId: null, publicPath: null, channelName: null};
  }

  /** Drop account-bound telemetry when authentication ownership changes. */
  reset() {
    if (this.flushTimer) clearTimeout(this.flushTimer);
    this.flushTimer = null;
    this.queue = [];
    this.flushFailures = 0;
    this.clearContext();
  }

  /**
   * Queue a health event. Safe to call from anywhere, never throws.
   *
   * @param {string} event   e.g. 'mpegts.error', 'video.stalled', 'slotAvailable.ignored'
   * @param {object} attrs   event-specific payload, must be JSON-safe
   */
  report(event, attrs = {}) {
    try {
      if (typeof event !== 'string' || event.length === 0) return;
      if (this.queue.length >= MAX_QUEUE) {
        // Drop oldest to bound memory.
        this.queue.shift();
      }
      this.queue.push({
        event: boundedString(event, 160),
        viewId: boundedString(this.context.viewId, 160),
        publicPath: boundedString(this.context.publicPath, 160),
        channelName: boundedString(this.context.channelName, 300),
        ts: Date.now(),
        attrs: this.sanitize(attrs)
      });
      if (this.queue.length >= FLUSH_THRESHOLD) {
        this.flush();
      } else {
        this.scheduleFlush();
      }
    } catch (_) {
      // Never let telemetry throw into a render or an event handler.
    }
  }

  async sendBatch(batch, {keepalive = false} = {}) {
    try {
      if (typeof fetch !== 'function') return false;
      const token = localStorage.getItem(process.env.REACT_APP_TOKEN_NAME);
      if (!token) return false;
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}${ENDPOINT}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        keepalive,
        body: JSON.stringify({events: batch})
      });
      if (!response.ok) return false;

      // A 202 response means the server handled the whole batch. `accepted`
      // and `dropped` together account for rate-limited/invalid rows, which
      // must not be retried indefinitely.
      try {
        const result = await response.clone().json();
        if (
          Number.isFinite(result?.accepted) &&
          Number.isFinite(result?.dropped) &&
          result.accepted + result.dropped < batch.length
        ) {
          return false;
        }
      } catch (_) {
        // Some reverse proxies legitimately return an empty accepted response.
      }
      return true;
    } catch (_) {
      return false;
    }
  }

  createTransportBatch() {
    const queuedEvents = [];
    const events = [];
    for (const queuedEvent of this.queue.slice(0, MAX_BATCH)) {
      const candidate = events.concat(queuedEvent);
      if (jsonByteLength({events: candidate}) <= MAX_PAYLOAD_BYTES) {
        queuedEvents.push(queuedEvent);
        events.push(queuedEvent);
        continue;
      }
      if (events.length === 0) {
        // A pathological single event must not block every later flush.
        queuedEvents.push(queuedEvent);
        events.push({...queuedEvent, attrs: {telemetryTruncated: true}});
      }
      break;
    }
    return {queuedEvents, events};
  }

  /** Force a flush (returns a promise resolved after one bounded batch). */
  flush(options = {}) {
    if (this.flushPromise) {
      return this.flushPromise;
    }
    if (this.queue.length === 0) return Promise.resolve();

    // Keep object references in the queue while the request is in flight.
    // Reports that arrive concurrently are untouched when this batch succeeds.
    const {queuedEvents, events} = this.createTransportBatch();
    if (events.length === 0) return Promise.resolve();
    this.flushPromise = this.sendBatch(events, options)
      .then((accepted) => {
        if (accepted) {
          const sent = new Set(queuedEvents);
          this.queue = this.queue.filter((event) => !sent.has(event));
          this.flushFailures = 0;
          if (this.queue.length > 0 && !options.keepalive) this.scheduleFlush();
        } else {
          this.flushFailures += 1;
          if (!options.keepalive) this.scheduleFlush();
        }
        return accepted;
      })
      .finally(() => {
        this.flushPromise = null;
      });
    return this.flushPromise;
  }

  scheduleFlush() {
    if (this.flushTimer) return;
    this.flushTimer = setTimeout(() => {
      this.flushTimer = null;
      this.flush();
    }, FLUSH_INTERVAL_MS);
  }

  sanitize(attrs) {
    // Provider URLs often embed usernames/passwords. Recursively strip URL,
    // token, header and client-identifying keys before anything enters the
    // in-memory queue, including nested objects produced by media libraries.
    if (!attrs || typeof attrs !== 'object') return {};
    const seen = new WeakSet();
    let remainingNodes = MAX_ATTR_NODES;
    const scrub = (value, depth = 0) => {
      if (remainingNodes <= 0) return undefined;
      remainingNodes -= 1;
      if (value === undefined || value === null) return undefined;
      if (typeof value === 'function' || typeof value === 'symbol' || typeof value === 'bigint')
        return undefined;
      if (typeof value === 'string') {
        const redacted = sanitizeAttrString(value);
        return redacted.length > 1000 ? `${redacted.substring(0, 1000)}…` : redacted;
      }
      if (typeof value !== 'object') return value;
      if (depth >= 6 || seen.has(value)) return undefined;

      seen.add(value);
      let output;
      if (Array.isArray(value)) {
        output = value
          .slice(0, MAX_ATTR_NODES)
          .map((item) => scrub(item, depth + 1))
          .filter((item) => item !== undefined);
      } else {
        output = {};
        for (const key of Object.keys(value).slice(0, MAX_ATTR_NODES)) {
          if (isSensitiveAttrKey(key)) continue;
          const safeValue = scrub(value[key], depth + 1);
          if (safeValue !== undefined) output[String(key).substring(0, 120)] = safeValue;
        }
      }
      seen.delete(value);
      return output;
    };

    const safe = scrub(attrs);
    return safe && !Array.isArray(safe) ? safe : {};
  }

  installUnloadHook() {
    if (typeof window === 'undefined') return;
    const handler = () => {
      if (this.queue.length === 0) return;
      try {
        // keepalive supports an Authorization header during tab teardown. The
        // queue is only removed asynchronously after acceptance.
        this.flush({keepalive: true});
      } catch (_) {
        // ignore
      }
    };
    window.addEventListener('pagehide', handler);
    window.addEventListener('beforeunload', handler);
  }
}

export const playerHealth = new PlayerHealthClient();
